import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, type UserRole } from '@/stores/auth';
import { apiClient } from '@/lib/api';

/**
 * Response types for auth endpoints
 */
interface AuthMeResponse {
  user: {
    id: string;
    email: string;
    role: UserRole;
    organizationId: string | null;
  };
  tokenExpiringSoon?: boolean;
}

interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    role: UserRole;
    organizationId: string | null;
  };
}

interface RefreshResponse {
  token?: string;
  user?: {
    id: string;
    email: string;
    role: UserRole;
    organizationId: string | null;
  };
  ok?: boolean;
  message?: string;
}

interface DistributionUserOrgLookupResponse {
  user_id: string;
  email: string;
  organization_id?: string | null;
  role: string;
}

interface VerifyResponse {
  valid: boolean;
  reason?: string;
  expiringSoon?: boolean;
  expiresAt?: string;
}

/**
 * Auth hook providing authentication operations
 */
export function useAuth() {
  const navigate = useNavigate();
  const { 
    token, 
    user, 
    isAuthenticated, 
    setAuth, 
    logout: storeLogout,
    updateUser,
  } = useAuthStore();

  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await apiClient.post<LoginResponse>('auth/login', {
        email,
        password,
      });

      const { token, user: authUser } = response;

      // Default org from unified auth, may be null
      let organizationId: string | null = authUser.organizationId;

      // Resolve organization_id from Distribution worker using email
      try {
        const distLookup = await apiClient.post<DistributionUserOrgLookupResponse>(
          'distribution/users/lookup-org',
          { email: authUser.email }
        );

        if (distLookup.organization_id) {
          organizationId = distLookup.organization_id;
        }
      } catch (lookupError) {
        console.warn('[Auth] Failed to resolve Distribution organization for user:', lookupError);
        // Fallback: keep authUser.organizationId
      }

      setAuth(token, {
        id: String(authUser.id),
        username: authUser.email.split('@')[0],
        email: authUser.email,
        role: authUser.role,
        organization_id: organizationId,
      });

      return true;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [setAuth]);

  /**
   * Logout and redirect to login page
   */
  const logout = useCallback(async () => {
    try {
      // Notify backend (optional, for audit logging)
      await apiClient.post('auth/logout');
    } catch {
      // Ignore errors during logout
    } finally {
      // Clear local auth state
      storeLogout();
      // Clear refresh interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      // Redirect to login
      navigate('/login');
    }
  }, [storeLogout, navigate]);

  /**
   * Verify current token is valid
   */
  const verifyToken = useCallback(async (): Promise<VerifyResponse> => {
    try {
      const response = await apiClient.get<VerifyResponse>('auth/verify');
      return response;
    } catch {
      return { valid: false, reason: 'verification_failed' };
    }
  }, []);

  /**
   * Fetch current user info
   */
  const fetchUser = useCallback(async (): Promise<boolean> => {
    try {
      const response = await apiClient.get<AuthMeResponse>('auth/me');
      
      if (response.user) {
        updateUser({
          id: response.user.id,
          email: response.user.email,
          role: response.user.role,
          // Prefer existing organization_id if already resolved from Distribution
          organization_id: response.user.organizationId ?? user?.organization_id ?? null,
        });

        // Check if token is expiring soon
        if (response.tokenExpiringSoon) {
          // Attempt to refresh token
          await refreshToken();
        }

        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [updateUser]);

  /**
   * Refresh the auth token
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await apiClient.post<RefreshResponse>('auth/refresh');
      
      if (response.token && response.user) {
        setAuth(response.token, {
          id: response.user.id,
          username: response.user.email.split('@')[0],
          email: response.user.email,
          role: response.user.role,
          // Preserve previously resolved Distribution organization_id if present
          organization_id: response.user.organizationId ?? user?.organization_id ?? null,
        });
        console.log('[Auth] Token refreshed successfully');
        return true;
      }
      
      return response.ok ?? false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, [setAuth]);

  /**
   * Check if user has any of the specified roles
   */
  const hasRole = useCallback((roles: UserRole | UserRole[]): boolean => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    
    // Normalize roles for comparison
    const normalizeRole = (role: string): string => {
      if (role === 'admin') return 'k-admin';
      if (role === 'client') return 'c-client';
      return role;
    };

    const userNormalizedRole = normalizeRole(user.role);
    return roleArray.some(role => {
      const normalizedRole = normalizeRole(role);
      return userNormalizedRole === normalizedRole || user.role === role;
    });
  }, [user]);

  /**
   * Check if user is admin (k-admin or legacy admin)
   */
  const isAdmin = useCallback((): boolean => {
    return hasRole(['admin', 'k-admin']);
  }, [hasRole]);

  /**
   * Set up auto-refresh for token
   * Checks every 5 minutes if token needs refresh
   */
  useEffect(() => {
    if (!isAuthenticated || !token) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    // Check token validity every 5 minutes
    const checkAndRefresh = async () => {
      const result = await verifyToken();
      if (result.valid && result.expiringSoon) {
        await refreshToken();
      } else if (!result.valid) {
        // Token is invalid, logout
        await logout();
      }
    };

    // Initial check
    checkAndRefresh();

    // Set up interval (5 minutes)
    refreshIntervalRef.current = setInterval(checkAndRefresh, 5 * 60 * 1000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, token, verifyToken, refreshToken, logout]);

  return {
    // State
    user,
    token,
    isAuthenticated,
    
    // Actions
    login,
    logout,
    fetchUser,
    refreshToken,
    verifyToken,
    
    // Helpers
    hasRole,
    isAdmin,
  };
}

export default useAuth;

